import { useEffect, useState } from 'react';
import { Clock, Pause } from 'lucide-react';
import { cn } from '../lib/cn';
import type { ActividadEstado } from '../lib/sesion';

/**
 * Temporizador con AUTORIDAD DE SERVIDOR (hito M1).
 *
 * El cliente NO manda sobre el reloj: calcula el countdown desde los campos que
 * el servidor emite por WS y alinea su "ahora" con `server_now` mediante un
 * offset (ver useSalaViva). Fórmula:
 *
 *   ahoraServidor = Date.now() + offsetMs
 *   transcurrido  = (ahoraServidor - abiertaAt) / 1000 - pausaAcumuladoS
 *   restante      = duracionMin*60 - transcurrido
 *
 * La PAUSA lo detiene: cuando estado !== 'abierta' dejamos de latir y el valor
 * queda congelado en el último cálculo (el acumulado de pausa se cierra al
 * reanudar, así que al volver a 'abierta' el resto es de nuevo correcto).
 */
export interface TiempoServidorArgs {
  abiertaAt: string | null | undefined;
  duracionMin: number | null | undefined;
  pausaAcumuladoS?: number | null;
  estado: ActividadEstado;
  /** ms a sumar a Date.now() para obtener la hora del servidor. */
  offsetMs?: number;
}

export interface TiempoServidor {
  texto: string;
  restanteSeg: number;
  vencido: boolean;
  corriendo: boolean;
  pausada: boolean;
}

function fmt(segundos: number): string {
  const s = Math.max(0, Math.floor(segundos));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** Hook de cálculo puro; alimenta al componente y a la barra XIA. */
export function useTiempoServidor({
  abiertaAt,
  duracionMin,
  pausaAcumuladoS = 0,
  estado,
  offsetMs = 0,
}: TiempoServidorArgs): TiempoServidor {
  const corriendo = estado === 'abierta' && Boolean(abiertaAt);
  const pausada = estado === 'pausada';

  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    if (!corriendo) return;
    // Al arrancar/reanudar tomamos "ahora" ya, y latimos cada segundo.
    setAhora(Date.now());
    const id = window.setInterval(() => setAhora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [corriendo, abiertaAt, pausaAcumuladoS]);

  const totalSeg = (duracionMin ?? 0) * 60;

  if (!abiertaAt) {
    return { texto: fmt(totalSeg), restanteSeg: totalSeg, vencido: false, corriendo: false, pausada: false };
  }

  const inicio = new Date(abiertaAt).getTime();
  const ahoraServidor = ahora + offsetMs;
  const transcurrido = (ahoraServidor - inicio) / 1000 - (pausaAcumuladoS ?? 0);
  const restante = totalSeg - transcurrido;

  return {
    texto: fmt(restante),
    restanteSeg: restante,
    vencido: restante <= 0,
    corriendo,
    pausada,
  };
}

type Tamano = 'md' | 'xl';

interface TemporizadorProps extends TiempoServidorArgs {
  /** 'xl' para la Pantalla (legible a 6 m); 'md' para Cabina. */
  tamano?: Tamano;
  className?: string;
}

/**
 * Componente MM:SS. En 'xl' se dimensiona para proyección. Cambia de tono
 * cuando queda poco (< 30 s) o vence, y muestra ícono de pausa al congelar.
 */
export function Temporizador({ tamano = 'md', className, ...args }: TemporizadorProps) {
  const t = useTiempoServidor(args);

  const tono = t.pausada
    ? 'text-warning'
    : t.vencido
      ? 'text-error'
      : t.restanteSeg <= 30 && t.corriendo
        ? 'text-warning'
        : 'text-content';

  const size = tamano === 'xl' ? 'text-4xl' : 'text-2xl';
  const iconSize = tamano === 'xl' ? 'h-9 w-9' : 'h-6 w-6';

  return (
    <span
      className={cn('inline-flex items-center gap-3 font-display font-bold tabular-nums', size, tono, className)}
      role="timer"
      aria-live="off"
      aria-label={t.pausada ? `En pausa, ${t.texto}` : `Tiempo restante ${t.texto}`}
    >
      {t.pausada ? (
        <Pause className={cn(iconSize, 'shrink-0')} aria-hidden="true" />
      ) : (
        <Clock className={cn(iconSize, 'shrink-0', t.corriendo ? 'text-cyan' : 'text-content-muted')} aria-hidden="true" />
      )}
      {t.texto}
    </span>
  );
}
